// src/components/ui/Badge.tsx

import React, { type ReactNode } from 'react';
import { clsx } from 'clsx';

interface BadgeProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  dot?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className,
  dot = false,
}) => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full';

  const variantClasses = {
    default: 'bg-primary/15 text-primary',
    success: 'bg-success/15 text-success',
    warning: 'bg-warn/15 text-warn',
    error:   'bg-destructive/15 text-destructive',
    info:    'bg-info/15 text-info',
    neutral: 'bg-muted text-foreground',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  const dotColors = {
    default: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warn',
    error:   'bg-destructive',
    info:    'bg-info',
    neutral: 'bg-muted-foreground',
  };

  return (
    <span
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {dot && (
        <span
          className={clsx(
            'w-1.5 h-1.5 rounded-full mr-1.5',
            dotColors[variant]
          )}
        />
      )}
      {children}
    </span>
  );
};

// Status Badge Component for common statuses
interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'pending' | 'error' | 'success' | 'warning' | 'healthy' | 'disconnected' | 'posted' | 'cancelled' | 'failed' | 'flagged' | 'reviewed';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showDot?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'sm',
  className,
  showDot = true,
}) => {
  const statusConfig = {
    active: { variant: 'success' as const, label: 'Active' },
    inactive: { variant: 'neutral' as const, label: 'Inactive' },
    pending: { variant: 'warning' as const, label: 'Pending' },
    error: { variant: 'error' as const, label: 'Error' },
    success: { variant: 'success' as const, label: 'Success' },
    warning: { variant: 'warning' as const, label: 'Warning' },
    healthy: { variant: 'success' as const, label: 'Healthy' },
    disconnected: { variant: 'error' as const, label: 'Disconnected' },
    posted: { variant: 'success' as const, label: 'Posted' },
    cancelled: { variant: 'neutral' as const, label: 'Cancelled' },
    failed: { variant: 'error' as const, label: 'Failed' },
    flagged: { variant: 'warning' as const, label: 'Flagged' },
    reviewed: { variant: 'info' as const, label: 'Reviewed' },
  };

  const config = statusConfig[status];

  return (
    <Badge
      variant={config.variant}
      size={size}
      className={className}
      dot={showDot}
    >
      {config.label}
    </Badge>
  );
};

// Numeric Badge Component for counts
interface NumericBadgeProps {
  count: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const NumericBadge: React.FC<NumericBadgeProps> = ({
  count,
  max = 99,
  variant = 'default',
  size = 'sm',
  className,
}) => {
  const displayCount = count > max ? `${max}+` : count.toString();

  if (count === 0) {
    return null;
  }

  return (
    <Badge
      variant={variant}
      size={size}
      className={clsx('min-w-[1.5rem] justify-center', className)}
    >
      {displayCount}
    </Badge>
  );
};

// Account Type Badge Component
interface AccountTypeBadgeProps {
  type: 'depository' | 'credit' | 'loan' | 'investment' | 'other';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AccountTypeBadge: React.FC<AccountTypeBadgeProps> = ({
  type,
  size = 'sm',
  className,
}) => {
  const typeConfig = {
    depository: { variant: 'success' as const, label: 'Checking/Savings' },
    credit: { variant: 'warning' as const, label: 'Credit Card' },
    loan: { variant: 'error' as const, label: 'Loan' },
    investment: { variant: 'info' as const, label: 'Investment' },
    other: { variant: 'neutral' as const, label: 'Other' },
  };

  const config = typeConfig[type];

  return (
    <Badge
      variant={config.variant}
      size={size}
      className={className}
    >
      {config.label}
    </Badge>
  );
};

// Amount Badge Component for transaction amounts
interface AmountBadgeProps {
  amount: number;
  isIncome?: boolean;
  currency?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AmountBadge: React.FC<AmountBadgeProps> = ({
  amount,
  isIncome = false,
  currency = 'USD',
  size = 'sm',
  className,
}) => {
  const isPositive = isIncome ? amount > 0 : amount < 0;
  const displayAmount = Math.abs(amount);
  const variant = isPositive ? 'success' : 'error';
  const sign = isPositive ? '+' : '-';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return (
    <Badge
      variant={variant}
      size={size}
      className={className}
    >
      {sign}{formatter.format(displayAmount)}
    </Badge>
  );
};

export { Badge };
export default Badge;